import { useCallback, useContext, useRef, useMemo } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { odata } from '@bingads-webui/http-util';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import {
  parsePerformanceEstimateAPI,
  parseBudgetSuggestionAPI,
  parseBudgetLandscapeAPI,
  getSuggestedBudgetAmounts,
} from './model/api-parser';
import { MODE, getParserByCampaignType } from './model/constants';
import { useBudgetMetricsLogger } from './components/use-budget-metrics-logger';

export const createOrUpdateCampaign = (data) => {
  const campaignEDM = edmCampaign.types.resolve('Model.ShoppingCampaign');
  const campaignResource = new campaignEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

export const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
      hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'AdInsight/V2' : 'AdInsight/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.AdInsightAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.AdInsightAPI}${odataPath}`,
  };

  return oDataConfig;
};

export const useBudgetSuggestionRepository = ({
  mode,
  campaignId,
  campaignType,
  currency,
  campaign,
  assetGroup,
}) => {
  const serverContext = useContext(ServerContext);

  const { scope } = useContext(ScopeContext) || {};

  const i18n = useContext(I18nContext);

  const { aid, cid, campaignId: campaignIdFromScope } = scope.values;
  const currentCampaignId = campaignId || campaignIdFromScope;

  const { CurrentAccount: { CurrencyCode } } = serverContext;

  const {
    logBudgetFetchMetrics,
    logBudgetLandscapeMetrics,
    logCustomBudgetPerformanceMetrics,
    saveBudgetSuggestionMetrics,
  } = useBudgetMetricsLogger();

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const oDataOptions = useMemo(() => ({
    contentType: 'application/json',
    path: oDataConfig.odataPath,
    oDataToken: oDataConfig.odataToken,
  }), [oDataConfig]);

  const latestSuggestedBudget = useRef([]);

  const fetchBudgetSuggestionData = useCallback((payload) => {
    const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/BudgetSuggestion.GetBudgetSuggestion`;

    const options = _.extend(
      {},
      oDataOptions,
      {
        url: baseURL,
        data: payload,
      }
    );

    const promise = odata.post(options);

    return promise.then((data) => {
      if (data) {
        const parsedBudgetSuggestion = parseBudgetSuggestionAPI(data, i18n, currency);

        logBudgetFetchMetrics({ campaign, budgetResponse: parsedBudgetSuggestion });
        latestSuggestedBudget.current = parsedBudgetSuggestion;
        return parsedBudgetSuggestion;
      }

      return null;
    });
  }, [aid, campaign, cid, logBudgetFetchMetrics, oDataConfig.odataPath, oDataOptions, i18n, currency]);

  const fetchInitialBudgetSuggestionData = useCallback(() => {
    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      assetGroup, campaign, campaignType, currency: currency || CurrencyCode,
    });

    return fetchBudgetSuggestionData(payload);
  }, [CurrencyCode, assetGroup, campaign, campaignType, currency, fetchBudgetSuggestionData]);

  const saveBudget = useCallback(({
    budgetAmount,
    budgetTier,
    biddingScheme,
  }) => {
    const modelToSave = {
      Id: campaignId,
      Budget: {
        Amount: _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount),
        Type: 'DailyStandard',
      },
      ...(
        _.isEmpty(biddingScheme) ? {} : { BiddingScheme: biddingScheme }
      ),
    };

    return createOrUpdateCampaign(modelToSave).then((res) => {
      saveBudgetSuggestionMetrics({
        campaign, budgetResponse: latestSuggestedBudget.current, appliedBudget: budgetAmount, budgetTier,
      });

      return res;
    }).catch(error =>
      // eslint-disable-next-line prefer-promise-reject-errors
      Promise.reject({ error }));
  }, [campaign, campaignId, i18n, saveBudgetSuggestionMetrics]);

  const fetchBudgetLandscapeData = useCallback(() => {
    const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Campaigns(${currentCampaignId})/Opportunity.GetUpdatedBudgetLandscape`;

    const promise = odata.get(baseURL, oDataConfig);

    return promise.then((data) => {
      const parsedBudgetLandscape = parseBudgetLandscapeAPI(data, i18n, currency);

      logBudgetLandscapeMetrics({ campaign: { Id: currentCampaignId }, budgetLandscapeResponse: parsedBudgetLandscape });

      return parsedBudgetLandscape;
    });
  }, [oDataConfig, cid, aid, currentCampaignId, i18n, currency, logBudgetLandscapeMetrics]);

  const loadBudget = useCallback(() => {
    if (mode === MODE.CREATION) {
      return fetchInitialBudgetSuggestionData();
    }

    return fetchBudgetLandscapeData();
  }, [fetchBudgetLandscapeData, fetchInitialBudgetSuggestionData, mode]);

  const fetchCustomBudgetPerformanceData = useCallback(({ budgetAmount }) => {
    const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/BudgetSuggestion.GetPerformanceEstimation`;

    const updatedCampaign = {
      ...campaign,
      Budget: {
        Amount: _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount),
      },
    };

    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      assetGroup,
      campaign: updatedCampaign,
      currency: currency || CurrencyCode,
      latestSuggestedBudget: getSuggestedBudgetAmounts(latestSuggestedBudget.current).sort((a, b) => a - b),
      isPerformanceEstimation: true,
    });

    const options = _.extend(
      {},
      oDataOptions,
      {
        url: baseURL,
        data: payload,
      }
    );

    const promise = odata.post(options);

    return promise.then((data) => {
      const parsedPerformanceEstimate = parsePerformanceEstimateAPI(data, i18n, currency);

      logCustomBudgetPerformanceMetrics({ performanceMetricsResponse: parsedPerformanceEstimate });

      return parsedPerformanceEstimate;
    });
  }, [CurrencyCode, aid, assetGroup, campaign, campaignType, cid, currency, i18n, logCustomBudgetPerformanceMetrics, oDataConfig.odataPath, oDataOptions]);

  // only for online model.
  // load budget but only updating few fields in campaign object.
  const loadBudgetPartial = useCallback((args) => {
    const updatedCampaign = {
      ...campaign,
      ...args,
    };

    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      assetGroup,
      campaign: updatedCampaign,
      campaignType,
      currency: currency || CurrencyCode,
    });

    return fetchBudgetSuggestionData(payload);
  }, [CurrencyCode, assetGroup, campaign, campaignType, currency, fetchBudgetSuggestionData]);

  return {
    loadBudget,
    loadBudgetPartial,
    saveBudget,
    fetchCustomBudgetPerformanceData,
  };
};
